import { Box, Stack, Typography } from '@mui/material';
import {
  GetAssemblyDetailsDocument,
  Maybe,
  useInlineUpdateSingleAssemblyTemplateFieldDataMutation,
} from '@/__generated__/graphql';
import { EquipmentDetailsTypography } from '../styles';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useMemo } from 'react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { LazyDatePickerCell } from '@/components/Analysis/shared/DatePickerCell';

type Props = {
  name: string | undefined;
  value: Maybe<string> | undefined;
  type: string | undefined;
  templateKey: Maybe<string> | undefined;
  data?: { [key: string]: string };
};

export const DatePickerForComponentSection = ({ name, value, templateKey, data }: Props) => {
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });

  const [mutate, { loading }] = useInlineUpdateSingleAssemblyTemplateFieldDataMutation({
    refetchQueries: [GetAssemblyDetailsDocument],
  });

  const allAssemblyTemplates = useAtomValue(state.allAssemblyTemplates);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);

  const template = allAssemblyTemplates?.find((t) => t.id === selectedAssemblyTemplateId);
  const field = template?.fields.find((f) => f.key === templateKey);

  // Check if the field has a detailFieldKey (assignment field) and get its value
  const subtitle = useMemo(() => {
    if (field?.detailFieldKey && data) {
      return data[field.detailFieldKey];
    }
    return undefined;
  }, [field?.detailFieldKey, data]);

  if (!field) return <Typography>N/A</Typography>;

  const handleDateSave = async (newDateValue: string) => {
    if (!selectedAssemblyId) {
      showSnackBar('Could not update. Missing selectedAssemblyId', 'error');
      throw new Error('Missing selectedAssemblyId');
    }

    if (!templateKey) {
      showSnackBar('Could not update. Missing templateKey', 'error');
      throw new Error('Missing templateKey');
    }

    if (!selectedAssemblyTemplateId) {
      showSnackBar('Could not update. Missing selectedAssemblyTemplateId', 'error');
      throw new Error('Missing selectedAssemblyTemplateId');
    }

    try {
      const response = await mutate({
        variables: {
          input: {
            assemblyId: selectedAssemblyId,
            key: templateKey,
            newValue: newDateValue,
            templateId: selectedAssemblyTemplateId,
            lastModifiedFor: `${name} update`,
          },
        },
      });
      const updatedValue =
        response?.data?.updateSingleTemplateFieldData?.templateFieldData?.[templateKey];

      // Empty string is a valid updated value (cleared date), so only treat `undefined` as invalid.
      if (updatedValue === undefined) {
        throw new Error('INVALID_RESPONSE');
      }

      showSnackBar('Updated successfully', 'success');
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'INVALID_RESPONSE') {
        showSnackBar('Could not update. Invalid response from server', 'error');
      } else {
        showSnackBar('Something went wrong. Please contact support', 'error');
      }
      throw error;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 1,
      }}
    >
      <Stack direction="column" alignItems="flex-start">
        <EquipmentDetailsTypography>{name}</EquipmentDetailsTypography>
        {subtitle && (
          <Typography fontSize={'1rem'} color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
            ({subtitle})
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          position: 'relative',
          minWidth: '100px',
          width: '50%',
        }}
      >
        <LazyDatePickerCell
          value={value}
          disabled={loading}
          variant="boxed"
          displayFormat="locale"
          showIcon={false}
          fontSize="1.2rem"
          ariaLabel={name ?? 'Date'}
          onSave={handleDateSave}
        />
      </Box>
    </Box>
  );
};
